import React, { createContext, useContext, useState, ReactNode } from "react";

// Language type
export type Language = "en" | "ar";

// Translation interface
interface Translations {
  pricing: {
    title: string;
    subtitle: string;
    topup: {
      name: string;
      credits: string;
      features: string[];
      duration: string;
    };
    premium: {
      name: string;
      badge: string;
      validFor: string;
      validityPeriod: string;
      includes: string;
      credits: string;
      features: string[];
    };
    premiumPlus: {
      name: string;
      badge: string;
      dealHeadline: string;
      regularValue: string;
      perMonthPrefix: string;
      perMonthSuffix: string;
      discountLabel: string;
      cta: string;
      reassurance: string;
      validFor: string;
      validityPeriod: string;
      bonus: string;
      includes: string;
      features: string[];
    };
    groupDiscount: {
      title: string;
      description: string;
      features: string[];
      whatsapp: string;
      email: string;
    };
    select: string;
    selected: string;
    proceedToPayment: string;
    termsText: string;
    termsLink: string;
    payment: {
      confirmationTitle: string;
      confirmationSubtitle: string;
      whatYouGet: string;
      backToPlans: string;
      paymentDetails: string;
      enterCardDetails: string;
      callbackSuccess: string;
      callbackFailed: string;
      successMessage: string;
      failureMessage: string;
      paymentId: string;
      status: string;
      message: string;
      startUsing: string;
      tryAgain: string;
      contactSupport: string;
    };
  };
  common: {
    sameDay: string;
    fullAccess: string;
    unlimitedCredits: string;
  };
}

// Translation data
const translations: Record<Language, Translations> = {
  en: {
    pricing: {
      title: "Choose your plan!",
      subtitle: "Select the perfect plan for your dental studies",
      topup: {
        name: "Top up",
        credits: "50 Credits",
        features: ["50 credits for AI chat & QBank", "Same day support"],
        duration: "One-time purchase",
      },
      premium: {
        name: "Premium",
        badge: "Most Popular",
        validFor: "Valid for",
        validityPeriod: "1 Month",
        includes: "Everything in Top-up +",
        credits: "Unlimited credits",
        features: ["Unlimited AI chats", "Unlimited QBank reveals", "Priority support"],
      },
      premiumPlus: {
        name: "Premium Plus",
        badge: "Best Value",
        dealHeadline: "3 months of Premium for less",
        regularValue: "3 monthly plans",
        perMonthPrefix: "Only",
        perMonthSuffix: "/ month",
        discountLabel: "less",
        cta: "Get 3 months & save",
        reassurance: "One payment • Instant access",
        validFor: "Valid for",
        validityPeriod: "3 Months",
        bonus: "Save",
        includes: "Everything in Premium for 3 months",
        features: ["Mock Exams"],
      },
      groupDiscount: {
        title: "Group Discount",
        description: "Special pricing for universities, study groups, and organizations",
        features: [
          "Discounts up to 30%",
          "Custom group pricing",
          "Dedicated group support",
          "Flexible billing options",
        ],
        whatsapp: "WhatsApp",
        email: "Email",
      },
      select: "Select",
      selected: "Selected",
      proceedToPayment: "Proceed To Payment",
      termsText: "By proceeding to the payment you agree to the",
      termsLink: "Terms and Conditions",
      payment: {
        confirmationTitle: "Payment Confirmation",
        confirmationSubtitle:
          "Review your plan details and complete your payment securely",
        whatYouGet: "What you get:",
        backToPlans: "← Back to Plans",
        paymentDetails: "Payment Details",
        enterCardDetails:
          "Enter your card details below to complete your purchase",
        callbackSuccess: "Payment Successful!",
        callbackFailed: "Payment Failed",
        successMessage:
          "Your payment has been processed successfully. Your credits have been added to your account and you can start using C-Shaped AI immediately!",
        failureMessage:
          "Please try again or contact our support team if the issue persists.",
        paymentId: "Payment ID:",
        status: "Status:",
        message: "Message:",
        startUsing: "Start Using C-Shaped",
        tryAgain: "Try Again",
        contactSupport: "Contact Support",
      },
    },
    common: {
      sameDay: "Same day support",
      fullAccess: "Full access to all dental specialities",
      unlimitedCredits: "Unlimited credits",
    },
  },
  ar: {
    pricing: {
      title: "اختر خطتك!",
      subtitle: "اختر الخطة المثالية لدراستك في طب الأسنان",
      topup: {
        name: "الشحن",
        credits: "50 رصيد",
        features: ["50 رصيد للذكاء الاصطناعي والأسئلة", "دعم في نفس اليوم"],
        duration: "عملية شراء لمرة واحدة",
      },
      premium: {
        name: "بريميوم",
        badge: "الأكثر شعبية",
        validFor: "صالح لمدة",
        validityPeriod: "شهر واحد",
        includes: "كل ما في الشحن +",
        credits: "رصيد غير محدود",
        features: ["محادثات ذكاء اصطناعي غير محدودة", "كشف أسئلة غير محدود", "دعم أولوية"],
      },
      premiumPlus: {
        name: "بريميوم بلس",
        badge: "أفضل قيمة",
        dealHeadline: "3 أشهر من البريميوم بسعر أقل",
        regularValue: "3 خطط شهرية",
        perMonthPrefix: "فقط",
        perMonthSuffix: "/ شهرياً",
        discountLabel: "أقل",
        cta: "احصل على 3 أشهر ووفّر",
        reassurance: "دفعة واحدة • وصول فوري",
        validFor: "صالح لمدة",
        validityPeriod: "3 أشهر",
        bonus: "وفّر",
        includes: "كل ما في البريميوم لمدة 3 أشهر",
        features: ["اختبارات محاكاة"],
      },
      groupDiscount: {
        title: "خصم المجموعات",
        description: "أسعار خاصة للجامعات ومجموعات الدراسة والمؤسسات",
        features: [
          "خصومات تصل إلى 30٪",
          "أسعار مخصصة للمجموعات",
          "دعم مخصص للمجموعة",
          "خيارات فوترة مرنة",
        ],
        whatsapp: "واتساب",
        email: "إيميل",
      },
      select: "اختيار",
      selected: "محدد",
      proceedToPayment: "متابعة الدفع",
      termsText: "بالمتابعة إلى الدفع فإنك توافق على",
      termsLink: "الشروط والأحكام",
      payment: {
        confirmationTitle: "تأكيد الدفع",
        confirmationSubtitle: "راجع تفاصيل خطتك وأكمل دفعتك بأمان",
        whatYouGet: "ما ستحصل عليه:",
        backToPlans: "← العودة للخطط",
        paymentDetails: "تفاصيل الدفع",
        enterCardDetails: "أدخل تفاصيل بطاقتك أدناه لإكمال عملية الشراء",
        callbackSuccess: "تم الدفع بنجاح!",
        callbackFailed: "فشل الدفع",
        successMessage:
          "تم معالجة دفعتك بنجاح. تم إضافة الرصيد إلى حسابك ويمكنك البدء في استخدام C-Shaped AI فوراً!",
        failureMessage:
          "يرجى المحاولة مرة أخرى أو الاتصال بفريق الدعم إذا استمرت المشكلة.",
        paymentId: "معرف الدفع:",
        status: "الحالة:",
        message: "الرسالة:",
        startUsing: "ابدأ استخدام C-Shaped",
        tryAgain: "حاول مرة أخرى",
        contactSupport: "اتصل بالدعم",
      },
    },
    common: {
      sameDay: "دعم في نفس اليوم",
      fullAccess: "وصول كامل لجميع تخصصات طب الأسنان",
      unlimitedCredits: "رصيد غير محدود",
    },
  },
};

// Language context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isRTL: boolean;
}

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Language provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("en");

  const isRTL = language === "ar";
  const t = translations[language];

  const value = {
    language,
    setLanguage,
    t,
    isRTL,
  };

  return (
    <LanguageContext.Provider value={value}>
      <div
        dir={isRTL ? "rtl" : "ltr"}
        style={{ direction: isRTL ? "rtl" : "ltr" }}
      >
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
